import {getCacheConfig} from '../config'
import {CacheDriverInterface} from '../types'

export default class BaseEngine implements CacheDriverInterface {
  /**
   * Storage engine
   */
  public storage: any

  /**
   * Prefix key
   */
  public prefixKey = ''

  /**
   * Value converter
   */
  protected valueConverter = this.convertValue.bind(this)

  /**
   * Value parser
   */
  protected valueParser = this.parseValue.bind(this)

  /**
   * Set value converter
   */
  public setValueConverter(converter: any): BaseEngine {
    this.valueConverter = converter

    return this
  }

  /**
   * Set value parser
   */
  public setValueParser(parser: any): BaseEngine {
    this.valueParser = parser

    return this
  }

  /**
   * {@inheritDoc}
   */
  set(key: string, value: any, expireAfter?: number | undefined): BaseEngine {
    const expireTime: number =
      expireAfter !== undefined ? expireAfter : getCacheConfig('expireAfter', 0)

    const expireAt = expireTime ? new Date().getTime() + expireTime * 1000 : undefined

    this.storage.setItem(
      this.getKey(key),
      this.valueConverter({
        data: value,
        expireAt,
      })
    )

    return this
  }

  /**
   * {@inheritDoc}
   */
  get(key: string, defaultValue?: any) {
    let value = this.storage.getItem(this.getKey(key))

    if (!value) return defaultValue

    try {
      const cacheData = this.valueParser(value)

      const now = new Date().getTime()

      if (cacheData.expireAt && cacheData.expireAt < now) {
        this.remove(key)

        return defaultValue
      }

      return cacheData.data
    } catch (error) {
      this.remove(key)
      return defaultValue
    }
  }

  /**
   * {@inheritDoc}
   */
  has(key: string): boolean {
    return Boolean(this.storage.getItem(this.getKey(key)))
  }

  /**
   * {@inheritDoc}
   */
  remove(key: string): BaseEngine {
    this.storage.removeItem(this.getKey(key))

    return this
  }

  /**
   * Get key with prefix
   */
  protected getKey(key: string) {
    return (this.getPrefixKey() || '') + key
  }

  /**
   * {@inheritDoc}
   */
  setPrefixKey(key: string): BaseEngine {
    this.prefixKey = key

    return this
  }

  /**
   * {@inheritDoc}
   */
  getPrefixKey(): string {
    return this.prefixKey
  }

  /**
   * Parse value
   *
   */
  protected parseValue(value: any) {
    try {
      return JSON.parse(value)
    } catch (error) {
      return value
    }
  }

  /**
   * the mechanism of the store value
   *
   * @returns
   */
  protected convertValue(value: any) {
    return JSON.stringify(value)
  }
}
